// Re-export React import for integration
import React from 'react'

import * as Sentry from '@sentry/react'

import { redactAccessLinkFragment } from '@/lib/access-link-fragment'
import { redactSensitive } from '@/lib/api-client/redact-sensitive'

declare const __APP_VERSION__: string

type NetworkPart = { body?: unknown }

type NetworkSpanData = { request?: NetworkPart; response?: NetworkPart }

const isNetworkSpanData = (data: unknown): data is NetworkSpanData =>
  typeof data === 'object' &&
  data !== null &&
  ('request' in data || 'response' in data)

const redactNetworkPart = (part: NetworkPart | undefined) =>
  part?.body === undefined
    ? part
    : { ...part, body: redactSensitive(part.body) }

const redactNetworkBodies = (
  event: Sentry.ReplayFrameEvent,
): Sentry.ReplayFrameEvent => {
  if (event.data.tag !== 'performanceSpan') {
    return event
  }

  const { payload } = event.data
  const { data } = payload
  if (!isNetworkSpanData(data)) {
    return event
  }

  return {
    ...event,
    data: {
      ...event.data,
      payload: {
        ...payload,
        data: {
          ...data,
          request: redactNetworkPart(data.request),
          response: redactNetworkPart(data.response),
        },
      },
    },
  }
}

/**
 * Reads a Sentry sample rate from an environment variable. Anything that is
 * not a finite number within [0, 1] falls back to `fallback` and warns, so a
 * variable missing from the deploy panel is visible instead of silent.
 */
const readSampleRate = (
  name: string,
  raw: unknown,
  fallback: number,
): number => {
  // Normalize before parsing: a whitespace-only string (e.g. an accidental
  // space in a deploy panel field) must be treated as empty, not as "0".
  const normalized = typeof raw === 'string' ? raw.trim() : raw
  const parsed = Number(normalized)

  if (
    normalized === undefined ||
    normalized === null ||
    normalized === '' ||
    !Number.isFinite(parsed) ||
    parsed < 0 ||
    parsed > 1
  ) {
    console.warn(
      `${name} is not a sample rate between 0 and 1. Falling back to ${fallback}.`,
    )
    return fallback
  }

  return parsed
}

/**
 * Initialize Sentry with enhanced configuration for better error tracking
 * and performance monitoring
 */
const withoutAccessLinkSecret = (
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb => ({
  ...breadcrumb,
  ...(breadcrumb.message && {
    message: redactAccessLinkFragment(breadcrumb.message),
  }),
  ...(breadcrumb.data && {
    data: Object.fromEntries(
      Object.entries(breadcrumb.data).map(([key, value]) => [
        key,
        typeof value === 'string' ? redactAccessLinkFragment(value) : value,
      ]),
    ),
  }),
})

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT
  const version = __APP_VERSION__
  const isProduction = environment === 'production'

  if (!dsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  if (!environment) {
    console.warn(
      'Sentry ENVIRONMENT not configured. Skipping Sentry initialization.',
    )
    return
  }

  if (!version) {
    console.warn('App VERSION not configured. Skipping Sentry initialization.')
    return
  }

  Sentry.init({
    dsn,
    environment,
    release: version,

    // Disable debug mode to reduce console noise
    debug: false,

    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Trace navigation and interactions
        traceFetch: true,
        traceXHR: true,
        enableHTTPTimings: true,
      }),

      // Session replay for debugging user sessions
      Sentry.replayIntegration({
        // Mask all text content by default for privacy
        maskAllText: isProduction,
        blockAllMedia: isProduction,
        // Network details help debug API issues
        networkDetailAllowUrls: [
          'localhost',
          /internal-api\..*\.signater\.com/,
        ],
        networkDetailDenyUrls: [/\/v1\/verifications\//],
        beforeAddRecordingEvent: redactNetworkBodies,
      }),

      // React-specific error boundary
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation: () => window.location,
        useNavigationType: () => 'POP',
        createRoutesFromChildren: () => [],
        matchRoutes: () => null,
      }),

      // Breadcrumbs for console logs
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true,
      }),
    ],

    // Performance Monitoring - Tracing
    // Rate is read from the env var, the same across all environments
    tracesSampleRate: readSampleRate(
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0,
    ),

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https?:\/\/internal-api\.dev\.signater\.com/,
      /^https?:\/\/internal-api\.signater\.com/,
      /^https?:\/\/verify-api(\.dev)?\.signater\.com/,
    ],

    // Session Replay
    // Rate is read from the env var, the same across all environments
    replaysSessionSampleRate: readSampleRate(
      'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0,
    ),
    // Replay-on-error defaults to 1: a variable missing from the panel must
    // not stop capturing the replay that explains a crash.
    replaysOnErrorSampleRate: readSampleRate(
      'VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1,
    ),

    // Log integration
    enableLogs: true,

    beforeBreadcrumb: withoutAccessLinkSecret,

    // Filter out certain errors that are not actionable
    beforeSend(event, hint) {
      // Filter out browser extension errors
      if (
        event.exception?.values?.[0]?.stacktrace?.frames?.some(
          (frame) =>
            frame.filename?.includes('chrome-extension://') ||
            frame.filename?.includes('moz-extension://') ||
            frame.filename?.includes('safari-extension://'),
        )
      ) {
        return null
      }

      // Filter out network errors that are likely user connectivity issues
      const error = hint.originalException
      if (error instanceof Error) {
        if (
          error.message.includes('Network Error') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError')
        ) {
          // Only send if it's not a simple connectivity issue
          event.fingerprint = ['network-error', error.message]
        }
      }

      if (event.request?.url) {
        event.request.url = redactAccessLinkFragment(event.request.url)
      }

      return event
    },

    // Ignore certain errors that are not actionable
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // React hydration warnings (usually not critical)
      'Hydration failed',
      // ResizeObserver loop errors (browser quirk, not actionable)
      'ResizeObserver loop completed',
      'ResizeObserver loop limit exceeded',
      // Common non-critical errors
      'Non-Error promise rejection captured',
      // Ad blockers
      'blocked by the client',
    ],

    // Tag all events with build information
    initialScope: {
      tags: {
        environment,
        release: version,
        build: import.meta.env.MODE,
      },
    },
  })
}
